import StorageService from "../services/StorageService.js";
import AccountSync from "../services/AccountSync.js";
import DailyChallenge from "../games/DailyChallenge.js";
import { RESUMABLE_MODES, poolSize } from "../utils/gameCatalog.js";
import { GAME_GROUPS } from "../utils/gameGroups.js";
import { escapeHtml, renderHearts } from "../utils/PlayerProfile.js";
import { showConfirmation } from "../utils/ConfirmationPopup.js";
import { PICTO_CLOCK } from "../utils/Pictos.js";

/** Icône par jeu, reprise du catalogue du classement — une seule source d'icônes. */
const ICONS = {
    ...Object.fromEntries(GAME_GROUPS.map((g) => [g.nameKey, g.icon])),
    // Le Lab : pas de groupe classé pour ce mode, même icône que la famille.
    "lab.shapeNerd": "/img/lab-shape.svg",
};

/** Couleur du mode, mêmes classes que les cartes de la home et le classement. */
const MODE_CLASSES = {
    "home.classic": "classic",
    "home.hard": "hard",
    "home.tricky": "tricky",
    "home.reverse": "reverse",
    "home.list": "list",
};

/**
 * Clés d'une partie à vies (BaseGame) : exactement celles que `restartGame()`
 * efface. Aucune n'est synchronisée (voir `isSyncableKey`), une partie en cours
 * ne quitte jamais l'appareil : les effacer localement suffit.
 */
const POOL_KEYS = ["countriesleft", "current", "proposals", "lifes", "finished", "startedAt", "errors"];

/**
 * Page /parties-en-cours : les parties commencées et non terminées, avec de quoi
 * les reprendre ou les abandonner.
 *
 * Tout est lu dans le localStorage : une partie en cours n'est pas synchronisée
 * (elle n'a de sens que sur l'appareil où elle a été commencée), et elle est
 * propre à la LANGUE courante, comme les clés qui la portent.
 */
export default class InProgress {
    constructor() {
        this.page = document.querySelector(".page.in-progress");
        if (!this.page) return;

        this.list = this.page.querySelector(".in-progress-list");
        this.lang = window.geoNerdApp.languageManager;
        this.render();
    }

    render() {
        this.runs = this.scan();
        if (this.runs.length === 0) {
            this.list.innerHTML = `
                <div class="in-progress-empty">
                    <p>${this.lang.t("inProgress.empty")}</p>
                    <a href="${this.href("")}" class="btn">${this.lang.t("inProgress.emptyCta")}</a>
                </div>
            `;
            return;
        }
        this.list.innerHTML = this.runs.map((run, i) => this.renderCard(run, i)).join("");
        this.list.querySelectorAll(".ip-abandon").forEach((btn) =>
            btn.addEventListener("click", () => this.confirmAbandon(this.runs[Number(btn.dataset.index)]))
        );
    }

    /**
     * Les parties en cours, sans la page /parties-en-cours (bandeau « Reprendre »
     * de la home) : le scan ne dépend que du localStorage et des données pays.
     */
    static scanAll() {
        return Object.create(InProgress.prototype).scan();
    }

    /** Toutes les parties en cours, dans l'ordre du catalogue. */
    scan() {
        const runs = [];
        for (const mode of RESUMABLE_MODES) {
            const run = mode.kind === "pool" ? this.scanPool(mode)
                : mode.kind === "letters" ? this.scanLetters(mode)
                : this.scanDaily(mode);
            if (Array.isArray(run)) runs.push(...run);
            else if (run) runs.push(run);
        }
        return runs;
    }

    /**
     * Jeux à vies. Deux traces possibles d'une partie réellement commencée : le
     * pool amputé d'au moins une bonne réponse, ou une vie perdue (une faute
     * n'écrit pas le pool). Ouvrir la page sans jouer ne laisse ni l'un ni
     * l'autre, seulement un `startedAt` — ce n'est pas une partie.
     */
    scanPool(mode) {
        const left = StorageService.load(`${mode.gameId}.countriesleft`);
        const remaining = Array.isArray(left) ? left.length : null;
        // US States List n'a pas de système de vies (voir UsStatesList.js) : pas de
        // décompte de cœurs à afficher, et rien à perdre : seul le pool fait foi.
        const lives = mode.noLives ? undefined : (() => {
            const lifes = StorageService.load(`${mode.gameId}.lifes`);
            return lifes === null ? 3 : parseInt(lifes, 10);
        })();

        // Pool vide = partie gagnée (l'écran de victoire la rejoue au rechargement),
        // plus de vies = partie perdue : ni l'une ni l'autre ne se reprend.
        if (remaining === 0 || (lives !== undefined && lives <= 0)) return null;
        if (remaining === null && (mode.noLives || lives === 3)) return null;

        const total = poolSize(mode, window.geoNerdApp);
        if (!total) return null;

        return {
            mode,
            found: remaining === null ? 0 : Math.max(0, total - remaining),
            total,
            lives,
            startedAt: StorageService.load(`${mode.gameId}.startedAt`),
        };
    }

    /** Country Nerd : une partie par lettre commencée (le chrono de la lettre existe). */
    scanLetters(mode) {
        const letters = window.geoNerdApp.letters || [];
        return letters.flatMap((letter) => {
            const startedAt = StorageService.load(`${mode.gameId}.letterStart.${letter}`);
            if (!startedAt) return [];
            if (StorageService.load(`${mode.gameId}.letter.${letter}`) === true) return [];

            const found = StorageService.load(`${mode.gameId}.progress.${letter}`, []);
            return [{
                mode,
                letter,
                found: Array.isArray(found) ? found.length : 0,
                total: (window.geoNerdApp.countriesByLetter[letter] || []).length,
                startedAt,
            }];
        });
    }

    /**
     * Défi du jour. La progression du jour est datée : celle d'hier est périmée
     * (le jeu la jette au chargement), et un défi déjà terminé aujourd'hui a
     * laissé son résultat daté.
     */
    scanDaily(mode) {
        const prefix = DailyChallenge.STORAGE_PREFIXES[mode.mode];
        const today = new Date().toISOString().slice(0, 10);
        if (localStorage.getItem(`${prefix}.${today}`) !== null) return null;

        let data = null;
        try {
            data = JSON.parse(localStorage.getItem(`${prefix}.progress`));
        } catch {
            return null;
        }
        if (!data || data.date !== today) return null;
        if (!data.currentIndex || data.currentIndex >= mode.total) return null;

        return { mode, found: data.currentIndex, total: mode.total, startedAt: data.startedAt };
    }

    renderCard(run, index) {
        const l = this.lang;
        const { mode } = run;
        const icon = ICONS[mode.gameNameKey];
        const modeClass = MODE_CLASSES[mode.modeKey] || "classic";
        const percent = run.total ? Math.round((run.found / run.total) * 100) : 0;

        // Une zone de Map Nerd ou une lettre de Country Nerd précise DE QUELLE
        // partie il s'agit : sans elle, quatre lignes « Map Nerd · Classic »
        // seraient indiscernables.
        const scope = mode.scopeKey ? l.t(mode.scopeKey)
            : run.letter ? l.t("inProgress.letterLabel").replace("{letter}", run.letter.toUpperCase())
            : "";

        const meta = [
            run.lives !== undefined
                ? `<span class="ip-meta-item">${renderHearts(3 - run.lives, (n) => l.t("leaderboard.livesLeft").replace("{n}", n))}</span>`
                : "",
            run.startedAt
                ? `<span class="ip-meta-item">${PICTO_CLOCK}${escapeHtml(this.formatStart(run.startedAt))}</span>`
                : "",
        ].filter(Boolean).join("");

        return `
            <article class="ip-card">
                <div class="ip-card-head">
                    ${icon ? `<img class="ip-icon" src="${icon}" alt="">` : ""}
                    <h2 class="ip-game">${escapeHtml(l.t(mode.gameNameKey))}</h2>
                    <span class="ip-mode ${modeClass}">${escapeHtml(l.t(mode.modeKey))}</span>
                    ${scope ? `<span class="ip-scope">${escapeHtml(scope)}</span>` : ""}
                </div>
                <div class="ip-progress">
                    <div class="ip-bar"><div class="ip-bar-fill" style="width: ${percent}%"></div></div>
                    <span class="ip-progress-value">${run.found}/${run.total}</span>
                </div>
                <div class="ip-meta">${meta}</div>
                <div class="ip-actions">
                    <a href="${this.href(mode.slug)}${mode.query || ""}" class="btn">${escapeHtml(l.t("inProgress.resume"))}</a>
                    ${mode.kind === "daily"
                        ? `<p class="ip-locked">${escapeHtml(l.t("inProgress.dailyLocked"))}</p>`
                        : `<button type="button" class="btn btn-secondary ip-abandon" data-index="${index}">${escapeHtml(l.t("inProgress.abandon"))}</button>`}
                </div>
            </article>
        `;
    }

    /**
     * URL d'une page de jeu, préfixe de langue compris. `GeoNerdNavigation` le
     * fait pour les liens du document, mais il s'exécute avant ce rendu : les
     * liens injectés ici doivent porter leur préfixe eux-mêmes. Les slugs de jeu
     * sont identiques dans les deux langues (aucun n'est dans `PAGE_SLUG_EN`).
     */
    href(slug) {
        return `${this.lang.currentLang === "en" ? "/en/" : "/"}${slug}`;
    }

    /** Date de début, en relatif court : l'heure exacte n'apprend rien. */
    formatStart(timestamp) {
        const l = this.lang;
        const days = Math.floor((Date.now() - timestamp) / 86400000);
        const when = days <= 0 ? l.t("inProgress.today")
            : days === 1 ? l.t("inProgress.yesterday")
            : l.t("inProgress.daysAgo").replace("{n}", days);
        return `${l.t("inProgress.startedLabel")} ${when}`;
    }

    confirmAbandon(run) {
        showConfirmation(
            this.lang.t("inProgress.abandonTitle"),
            this.lang.t("inProgress.abandonMessage"),
            () => this.abandon(run)
        );
    }

    abandon(run) {
        const { mode } = run;
        if (mode.kind === "pool") {
            POOL_KEYS.forEach((suffix) => StorageService.remove(`${mode.gameId}.${suffix}`));
        } else if (mode.kind === "letters") {
            ["progress", "letterStart", "letterFaults", "letterHints"].forEach((kind) =>
                StorageService.remove(`${mode.gameId}.${kind}.${run.letter}`)
            );
            // `progress.{lettre}` est la seule clé d'une partie en cours qui soit
            // synchronisée, et son merge est une UNION : effacée localement, elle
            // reviendrait telle quelle à la prochaine synchro. Il faut donc la
            // supprimer aussi côté serveur.
            AccountSync.deleteRemote([this.lang.getStorageKey(`${mode.gameId}.progress.${run.letter}`)]);
        }
        this.render();
    }
}
